/**
 * =============================================================================
 * useWlBillingKpis
 *
 * Hooks do painel WL -> Financeiro -> Cobrança. Consomem a RPC
 * `wl_get_billing_kpis` (mesma fonte de verdade) para:
 *   - KPIs de fluxo de caixa consolidado (receita, recebido, a receber, vencidos)
 *   - Séries de tendência (12 meses)
 *   - Aging de inadimplência
 *   - Drill-down de aging (lista paginada por bucket)
 *   - Drill-down dos 4 KPIs principais (v1.2.0)
 *
 * @version 1.2.0
 * RELEASE:
 *   2026-07-22 v1.0.0 - Versão inicial.
 *   2026-07-22 v1.1.0 - Adiciona `useWlBillingAgingDetails` (mesma RPC,
 *                       parâmetros opcionais de drill-down do aging).
 *   2026-07-22 v1.2.0 - Adiciona `useWlBillingKpiDetails` (drill-down dos
 *                       4 KPIs principais: receita_total, total_recebido,
 *                       a_receber, vencidos). Reaproveita a mesma RPC.
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/** KPI simples { value, count }. */
export interface WlBillingKpiValue {
  value: number;
  count: number;
}

/** Ponto de série mensal. */
export interface WlBillingTrendPoint {
  month: string;
  value: number;
}

/** Chaves válidas de bucket do aging. */
export type WlBillingAgingBucket =
  | 'future'
  | '1_30'
  | '31_60'
  | '61_90'
  | '91_120'
  | '120_plus';

/** Item do aging. */
export interface WlBillingAgingRow {
  bucket: WlBillingAgingBucket;
  label: string;
  count: number;
  total: number;
}

/** Linha da lista de drill-down do aging (uma parcela). */
export interface WlBillingAgingDetailRow {
  parcela_id: string;
  cobranca_id: string;
  /** Nº da Fatura (financeiro_parcelas.numero_fatura). v1.3.0 */
  numero_fatura: string | null;
  numero_parcela: number;
  total_parcelas: number | null;
  valor: number;
  data_vencimento: string;
  dias_vencido: number;
  status: string;
  tenant_id: string | null;
  tenant_name: string | null;
  tenant_slug: string | null;
  customer_name: string | null;
}

/** Payload paginado do drill-down. */
export interface WlBillingAgingDetailsPayload {
  bucket: WlBillingAgingBucket;
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
  rows: WlBillingAgingDetailRow[];
}

/** Payload completo da RPC. */
export interface WlBillingKpisPayload {
  reference_month: string;
  timezone: string;
  period: { start: string; end: string };
  kpis: {
    receita_total: WlBillingKpiValue;
    total_recebido: WlBillingKpiValue;
    a_receber: WlBillingKpiValue;
    vencidos: WlBillingKpiValue;
  };
  trend: {
    inadimplencia_pct: WlBillingTrendPoint[];
    receitas_perdidas: WlBillingTrendPoint[];
    ticket_medio: WlBillingTrendPoint[];
    taxa_recebimento: WlBillingTrendPoint[];
  };
  aging: WlBillingAgingRow[];
  /** Distribuição de parcelas em aberto por meio de pagamento (v1.4.0 da RPC). */
  payment_methods: WlBillingPaymentMethodRow[];
  aging_details: WlBillingAgingDetailsPayload | null;
}

/**
 * Linha do agrupamento por meio de pagamento (base = parcelas em aberto).
 * Exposto pela RPC `wl_get_billing_kpis` v1.4.0.
 */
export interface WlBillingPaymentMethodRow {
  meio_pagamento_id: string | null;
  nome: string;
  count: number;
  total: number;
}

/** Chaves válidas de drill-down por KPI (v1.2.0). */
export type WlBillingKpi = 'receita_total' | 'total_recebido' | 'a_receber' | 'vencidos';

/** Linha do drill-down de KPI. Superset de AgingDetailRow (inclui data_pagamento). */
export interface WlBillingKpiDetailRow extends WlBillingAgingDetailRow {
  data_pagamento: string | null;
}

/** Payload paginado do drill-down de KPI. */
export interface WlBillingKpiDetailsPayload {
  kpi: WlBillingKpi;
  label: string;
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
  rows: WlBillingKpiDetailRow[];
}

/**
 * Hook principal — KPIs + trend + aging.
 *
 * @param referenceMonth - Mês de referência ISO (yyyy-MM-dd). Null => mês atual.
 * @param timezone       - Timezone das agregações.
 * @param monthsBack     - Quantos meses na série de tendência (default 12).
 */
export function useWlBillingKpis(
  referenceMonth?: string | null,
  timezone: string = 'America/Sao_Paulo',
  monthsBack: number = 12,
) {
  return useQuery({
    queryKey: ['wl-billing-kpis', referenceMonth ?? 'current', timezone, monthsBack],
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<WlBillingKpisPayload> => {
      const { data, error } = await supabase.rpc('wl_get_billing_kpis', {
        p_reference_month: referenceMonth ?? null,
        p_timezone: timezone,
        p_months_back: monthsBack,
      } as never);
      if (error) throw error;
      return data as unknown as WlBillingKpisPayload;
    },
  });
}

/**
 * Hook de drill-down do aging.
 *
 * Reaproveita a mesma RPC `wl_get_billing_kpis` passando os parâmetros
 * opcionais de drill-down. Só executa quando `bucket` é informado.
 *
 * @param bucket   - Bucket do aging a detalhar (obrigatório para habilitar).
 * @param page     - Página 1-based.
 * @param perPage  - Itens por página (máx. 200 no backend).
 * @param search   - Busca por tenant / cliente / número de cobrança.
 * @param timezone - Timezone.
 */
export function useWlBillingAgingDetails(
  bucket: WlBillingAgingBucket | null,
  page: number = 1,
  perPage: number = 30,
  search: string = '',
  timezone: string = 'America/Sao_Paulo',
) {
  return useQuery({
    queryKey: ['wl-billing-aging-details', bucket, page, perPage, search, timezone],
    enabled: !!bucket,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<WlBillingAgingDetailsPayload> => {
      const { data, error } = await supabase.rpc('wl_get_billing_kpis', {
        p_reference_month: null,
        p_timezone: timezone,
        p_months_back: 1,
        p_aging_bucket: bucket,
        p_aging_page: page,
        p_aging_per_page: perPage,
        p_aging_search: search?.trim() ? search.trim() : null,
      } as never);
      if (error) throw error;
      const payload = data as unknown as WlBillingKpisPayload;
      // O drill-down é a chave `aging_details` do payload principal.
      if (!payload.aging_details) {
        return {
          bucket: bucket as WlBillingAgingBucket,
          page,
          per_page: perPage,
          total: 0,
          total_pages: 0,
          rows: [],
        };
      }
      return payload.aging_details;
    },
  });
}

/**
 * Hook de drill-down dos 4 KPIs principais da aba Cobrança (v1.2.0).
 * Reaproveita a mesma RPC `wl_get_billing_kpis`, retornando `kpi_details`.
 *
 * @param kpi              - Qual KPI detalhar (habilita a chamada).
 * @param page             - Página 1-based.
 * @param perPage          - Itens por página (máx. 200 no backend).
 * @param search           - Busca por tenant / cliente / ID de cobrança.
 * @param referenceMonth   - Mês de referência (mesmo do card). Null => atual.
 * @param timezone         - Timezone.
 */
export function useWlBillingKpiDetails(
  kpi: WlBillingKpi | null,
  page: number = 1,
  perPage: number = 30,
  search: string = '',
  referenceMonth?: string | null,
  timezone: string = 'America/Sao_Paulo',
) {
  return useQuery({
    queryKey: [
      'wl-billing-kpi-details',
      kpi,
      page,
      perPage,
      search,
      referenceMonth ?? 'current',
      timezone,
    ],
    enabled: !!kpi,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<WlBillingKpiDetailsPayload> => {
      const { data, error } = await supabase.rpc('wl_get_billing_kpis', {
        p_reference_month: referenceMonth ?? null,
        p_timezone: timezone,
        p_months_back: 1,
        p_aging_page: page,
        p_aging_per_page: perPage,
        p_aging_search: search?.trim() ? search.trim() : null,
        p_kpi: kpi,
      } as never);
      if (error) throw error;
      const payload = data as unknown as WlBillingKpisPayload & {
        kpi_details: WlBillingKpiDetailsPayload | null;
      };
      if (!payload.kpi_details) {
        return {
          kpi: kpi as WlBillingKpi,
          label: '',
          page,
          per_page: perPage,
          total: 0,
          total_pages: 0,
          rows: [],
        };
      }
      return payload.kpi_details;
    },
  });
}
